import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const InstallContext = createContext(null);
const STORAGE_KEY = "angiolini_install_prompt";
const SUPPRESS_MS = 7 * 86400 * 1000;

const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches
  || navigator.standalone === true;
const iosDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent)
  || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
const recentDismissal = () => {
  try {
    const time = Number(localStorage.getItem(STORAGE_KEY));
    return time > 0 && Date.now() - time < SUPPRESS_MS;
  } catch { return false; }
};

// Capture the browser event early, including before login, without displaying
// anything on public/auth pages. The inline UI lives ONLY in the client home.
export const InstallProvider = ({ children }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(standalone);
  const [dismissed, setDismissed] = useState(recentDismissal);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const busyRef = useRef(false);
  const [isIos] = useState(iosDevice);
  const [isSafari] = useState(() => /Version\/.*Safari/.test(navigator.userAgent)
    && !/CriOS|FxiOS|EdgiOS|OPiOS|Instagram|FBAN|FBAV/.test(navigator.userAgent));
  const dismiss = () => {
    setDismissed(true); setError(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* private browsing */ }
  };

  useEffect(() => {
    const media = window.matchMedia?.("(display-mode: standalone)");
    const onBeforeInstall = event => {
      event.preventDefault();
      setDeferred(event); setError(false);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); dismiss(); };
    const onDisplayMode = () => { if (standalone()) setInstalled(true); };
    const onStorage = event => {
      if (event.key === STORAGE_KEY || event.key === null) setDismissed(recentDismissal());
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("storage", onStorage);
    media?.addEventListener?.("change", onDisplayMode);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("storage", onStorage);
      media?.removeEventListener?.("change", onDisplayMode);
    };
  }, []);

  const install = async () => {
    if (!deferred || busyRef.current || installed) return;
    busyRef.current = true; setPending(true); setError(false);
    const event = deferred;
    setDeferred(null); // A browser installation event is single-use.
    try {
      await event.prompt();
      await event.userChoice;
      // Accepted OR dismissed: do not nag again; appinstalled confirms install.
      dismiss();
    } catch { setError(true); }
    finally { busyRef.current = false; setPending(false); }
  };

  return <InstallContext.Provider value={{ isIos, isSafari, pending, error, install, dismiss,
    canPrompt: !!deferred,
    eligible: !installed && !dismissed && (isIos || !!deferred || pending || error) }}>
    {children}
  </InstallContext.Provider>;
};

export const useInstall = () => useContext(InstallContext);